// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CheckboxStyle } from "@insite/mobius/Checkbox";
import CheckboxPresentationPropsDefault from "@insite/mobius/Checkbox/presentationProps";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";

import Check from "@insite/mobius/Icons/Check";

import { fontConfig } from "@eisinc/XDL/PreStyle/typography";
import { xBaseTheme } from "@eisinc/XDL/types";
import color from "@eisinc/XDL/Utilities/color";
import { css } from "styled-components";

export const CheckboxInputSelect = `span[role="checkbox"]`;

export const CheckboxCheckedSelector = `
    &[aria-checked="true"]:not([aria-disabled="true"]),
    &[aria-checked="mixed"]:not([aria-disabled="true"])
`;

export const CheckboxDefaultSelector = `&[aria-checked="false"]:not([aria-disabled="true"])`;
export const CheckboxDisabledSelector = `&[aria-disabled="true"]`;

const defaultStateStyles = css`
    & ${CheckboxInputSelect} {
        height: 14px;
        min-width: 14px;
        width: 14px;
        background: ${color("common.white")};
        border: 1px solid ${color("common.ui4")};
        border-radius: 1px;
        transition: all 0.3s ease;
        &:focus {
            box-shadow: none;
        }
        & + label {
            margin-left: 6px;
                line-height: ${fontConfig.xxSmall.lineHeight};
            color: ${color("common.ui6")};
        }
    }
`;

const checkedStyle = css`
    & ${CheckboxInputSelect} {
        ${CheckboxCheckedSelector} {
            background: ${color("primary.main")};
            border: 1px solid ${color("primary.main")};
            color: ${color("common.white")};
            justify-content: center;
        }
    }
`;
const hoverStyle = css`
    &:hover {
        ${CheckboxInputSelect} {
            transition: all 0.3s ease;
            ${CheckboxCheckedSelector} {
                background: ${color("common.ui8")};
                border: 1px solid ${color("common.ui8")};
            }
            ${CheckboxDefaultSelector} {
                background: ${color("common.white")};
                border: 1px solid ${color("common.ui8")};
            }
        }
    }
`;

const disabledStyle = css`
    ${CheckboxInputSelect} {
        ${CheckboxDisabledSelector} {
            background: ${color("common.ui2", 0.33)};
            border: 1px solid ${color("common.ui4", 0.33)};
            cursor: not-allowed;
            justify-content: center;
        }
    }
`;

export default {
    defaultProps: {
        ...CheckboxPresentationPropsDefault,
        mergeCss: true,
        typographyProps: {
            mergeCss: true,
            ...fontConfig.xSmall,
            weight: fontConfig.weight.regular,
            color: "text.main",
            css: css`
                letter-spacing: 0;
                user-select: none;
            `,
        },
        iconProps: {
            src: Check,
            size: fontConfig.xxSmall.size,
            ...({ height: fontConfig.xxSmall.size, width: fontConfig.xxSmall.size } as any),
        },
        css: css`
            &:not(:disabled) {
                cursor: pointer;
            }
            &&&[disabled] {
                cursor: not-allowed;
            }

            ${({ _labelPosition }: any) =>
                _labelPosition === "left" &&
                css`
                    margin-left: 0;
                `}

            ${defaultStateStyles}
            ${hoverStyle}
            ${checkedStyle}
            ${disabledStyle}
        `,
    },
    groupDefaultProps: {
        labelProps: {
            ...fontConfig.singleLineSmall,
            weight: fontConfig.weight.medium,
            css: css`
                letter-spacing: 0;
            `,
        },
        errorProps: {
            css: css`
                &:empty {
                    display: none;
                }
            `,
        },
        css: css`
            & > ${TypographyStyle as any} {
                &:first-child {
                    margin-bottom: 12px;
                }
                &:empty {
                    display: none;
                }
            }
            ${/* sc-selector */ CheckboxStyle} + ${/* sc-selector */ CheckboxStyle} {
                margin-top: 12px;
            }
        `,
    },
} as xBaseTheme["checkbox"];
